<template>
  <div>
    <v-container>
      <v-row align="center">
        <v-col cols="6">
          <div class="text-h5 ma-2">Account Type Mapping Rules
          </div>
        </v-col>
      </v-row>
      <v-divider/>
      <v-row align="center">
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="rules"
            :loading="loading"
            :footer-props="footerProps"
            :hide-default-footer="false"
            item-key="id"
            :custom-sort="customSort"
            fixed-header
            id="accountTypeTable">
              <template v-slot:item="{ item }">
                <tr>
                  <td><account-type-mapping-rule-dialog :rule="item"
                    @success="onRuleSaved" :existingRules="rules"
                    :accountTypeMap="accountTypeMap" isEditButton="true"/></td>
                  <td style="width: 30%">
                    {{ accountTypeMap.get(item.kasasaAccountType) }}</td>
                  <td style="width: 40%">{{ item.coreAccountTypes.join("; ") }}</td>
                  <td>
                    <v-btn icon @click="onDeleteRule(item)">
                      <v-icon>fa-trash</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>

            </v-data-table>
          </v-col>
        </v-row>

        <v-row v-if="!loading">
          <v-col>
            <span class="pr-4">
              <account-type-mapping-rule-dialog :existingRules="rules"
                :accountTypeMap="accountTypeMap"
                @success="onRuleSaved"/>
            </span>
          </v-col>
        </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { AxiosResponse } from 'axios';
import {
  Component, Watch, Inject, Provide,
} from 'vue-property-decorator';
import {
  AccountTypeMappingResource, AccountTypeMappingRule,
  AccountTypeMappingRuleResource,
} from '@/api/accounttypes/';
import { ConfigVersion } from '@/api/configversion/';
import AccountTypeMappingRuleDialog
  from '@/components/AccountTypeMappingRuleDialog.vue';
import { Alert, NoticeClass, Dialog } from '@kasasa/fbase-components/lib';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';

@Component({
  computed: mapState([
    'fiId', 'configVersion',
  ]),
  components: {
    'account-type-mapping-rule-dialog': AccountTypeMappingRuleDialog,
  },
})
export default class AccountTypes extends Vue {
  fiId!:string;

  configVersion!:ConfigVersion;

  accountTypeMap:Map<string, string> = new Map<string, string>();

  rules:AccountTypeMappingRule[] = [];

  loading:boolean = true;

  headers:DataTableHeader[] = [
    { text: '', value: 'edit' },
    { text: 'Kasasa Account Type', value: 'kasasaAccountType' },
    { text: 'Core Account Types', value: 'coreAccountTypes' },
    { text: '', value: 'delete' },
  ];

  footerProps = {
    'items-per-page-options': [{ value: -1, text: 'All' }, 25, 50, 100],
  }

  @Inject('AccountTypeMappingResource')
  private mappingResource!: AccountTypeMappingResource;

  @Inject('AccountTypeMappingRuleResource')
  ruleResource!:AccountTypeMappingRuleResource;

  @Watch('configVersion')
  mounted() {
    this.loading = false;
    this.mappingResource.getAll()
      .then((result:Map<string, string>) => {
        this.accountTypeMap = result;
        this.loadRules();
      })
      .catch((error:any) => {
        this.showError(error);
        this.loading = false;
      });
  }

  loadRules() {
    this.rules = [];
    this.loading = true;
    this.ruleResource.getAll(this.fiId)
      .then((result:AccountTypeMappingRule[]) => {
        this.rules = result;
        this.loading = false;
      })
      .catch((error:any) => {
        this.loading = false;
        this.showError(error);
      });
  }

  showError(error:any) {
    if (error.response && error.response.data && error.response.data.entity) {
      this.setError(error.response.data.entity);
    } else {
      this.setError('An unexpected error occurred.');
    }
  }

  setError(message:string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  onRuleSaved(savedRule:AccountTypeMappingRule) {
    let found:boolean = false;
    for (let i:number = 0; i < this.rules.length; i++) {
      if (this.rules[i].id === savedRule.id) {
        // Saved an existing rule, so make sure that it is up to date.
        Object.assign(this.rules[i], savedRule);
        found = true;
        break;
      }
    }
    if (!found) {
      const rule = savedRule;
      this.rules.push(rule);
    }
  }

  customSort = (items:AccountTypeMappingRule[], sortBy:string[],
    sortDesc:boolean[]) : AccountTypeMappingRule[] => {
    items.sort((a:any, b:any) => {
      let sortVal:number = 0;
      if (sortBy) {
        for (let i:number = 0; i < sortBy.length; i++) {
          if (a[sortBy[i]] !== b[sortBy[i]]) {
            if (sortDesc[i]) {
              sortVal = a[sortBy[i]] < b[sortBy[i]] ? 1 : -1;
            } else {
              sortVal = a[sortBy[i]] < b[sortBy[i]] ? -1 : 1;
            }
            break;
          } else {
            sortVal = 0;
          }
        }
      }
      return sortVal;
    });
    return items;
  }

  onDeleteRule(rule:AccountTypeMappingRule) {
    const that = this;
    const dialog = new Dialog('Confirm Deletion',
      'Are you sure you want to delete this account type mapping rule?');
    dialog.setAcceptLabel('OK')
      .setDeclineLabel('Cancel');
    this.$store.dispatch('notices/add', dialog)
      .then((ok) => {
        if (ok === 'accept') {
          that.ruleResource.remove(that.fiId, rule.id as number)
            .then((result:AxiosResponse) => {
              that.removeRuleFromList(rule);
            });
        }
      });
  }

  removeRuleFromList(rule:AccountTypeMappingRule) {
    for (let i:number = 0; i < this.rules.length; i++) {
      if (this.rules[i].id === rule.id) {
        this.rules.splice(i, 1);
        break;
      }
    }
  }
}
</script>

<style lang="scss">
tbody tr:nth-of-type(odd) {
   background-color: rgba(31, 25, 25, 0.02);
   background-blend-mode: multiply;
 }

#accountTypeTable
.v-data-table__wrapper{max-height:calc(100vh - 400px) !important;}

</style>
